import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TemplatePicker } from "@/components/TemplatePreview";
import { CaptionWordEditor } from "@/components/CaptionWordEditor";
import { ClipEditAssistant } from "@/components/ClipEditAssistant";
import { Loader2, Scissors } from "lucide-react";
import { toast } from "sonner";

export function ClipEditor({ clip, open, onOpenChange, onEdited }) {
  const [duration, setDuration] = useState(clip.end + 20);
  const [range, setRange] = useState([clip.start, clip.end]);
  const [captions, setCaptions] = useState(clip.captions_enabled ?? clip.captioned !== false);
  const [position, setPosition] = useState(clip.caption_position || "bottom");
  const [offset, setOffset] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templateId, setTemplateId] = useState(clip.template_id || "");
  const [headline, setHeadline] = useState(clip.hook_enabled !== false);
  const [autoFraming, setAutoFraming] = useState(clip.auto_framing !== false);
  const [aspect, setAspect] = useState(clip.aspect_ratio || "9:16");
  const [busy, setBusy] = useState(false);
  const [captionEdits, setCaptionEdits] = useState({});
  const [captionDirty, setCaptionDirty] = useState(false);
  const saving = useRef(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/projects/${clip.project_id}`)
      .then(({ data }) => {
        if (data.project?.duration) setDuration(data.project.duration);
        setTemplateId(clip.template_id || data.project?.template_id || "tmpl_hormozi");
      }).catch(() => {});
    setTemplatesLoading(true);
    api.get('/templates').then(({ data }) => setTemplates(data)).catch(() => toast.error("Could not load styles.")).finally(() => setTemplatesLoading(false));
    setRange([clip.start, clip.end]);
    setCaptions(clip.captions_enabled ?? clip.captioned !== false);
    setCaptionEdits(Object.fromEntries((clip.caption_edits || []).map((edit) => [edit.word_index, edit.text])));
    setCaptionDirty(false);
    setPosition(clip.caption_position || "bottom");
    setOffset(clip.caption_style?.margin_v || 0);
    setAspect(clip.aspect_ratio || "9:16");
    setHeadline(clip.hook_enabled !== false);
    setAutoFraming(clip.auto_framing !== false);
  }, [open, clip]);

  const changeTemplate = (id) => {
    setTemplateId(id);
    const template = templates.find(t => t.id === id);
    setPosition(template?.style?.position || "bottom");
    setOffset(0);
  };

  const save = async () => {
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    try {
      const { data } = await api.post(`/clips/${clip.id}/edit`, {
        start: range[0],
        end: range[1],
        captions_enabled: captions,
        caption_position: position,
        caption_offset: position === "center" ? 0 : offset,
        aspect_ratio: aspect,
        template_id: templateId || null,
        hook_enabled: headline,
        auto_framing: autoFraming,
        ...(captionDirty ? { caption_edits: Object.entries(captionEdits).map(([word_index, text]) => ({ word_index: Number(word_index), text })) } : {}),
      });
      toast.success("Clip saved — your corrected video is ready to download.");
      onEdited && onEdited(data);
      onOpenChange(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not re-render clip.");
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  const editorState = () => ({
    start: range[0], end: range[1], captions_enabled: captions, caption_position: position,
    caption_offset: position === "center" ? 0 : offset, aspect_ratio: aspect, template_id: templateId || null,
    hook_enabled: headline, auto_framing: autoFraming,
  });

  const applyAssistantChanges = (changes) => {
    if ("start" in changes || "end" in changes) setRange([changes.start ?? range[0], changes.end ?? range[1]]);
    if ("captions_enabled" in changes) setCaptions(changes.captions_enabled);
    if ("caption_position" in changes) setPosition(changes.caption_position);
    if ("caption_offset" in changes) setOffset(changes.caption_offset);
    if ("aspect_ratio" in changes) setAspect(changes.aspect_ratio);
    if ("template_id" in changes) setTemplateId(changes.template_id);
    if ("hook_enabled" in changes) setHeadline(changes.hook_enabled);
    if ("auto_framing" in changes) setAutoFraming(changes.auto_framing);
    if (changes.caption_edits?.length) {
      setCaptionEdits((prev) => ({ ...prev, ...Object.fromEntries(changes.caption_edits.map((e) => [e.word_index, e.text])) }));
      setCaptionDirty(true);
    }
  };

  const renderAfterAssistant = useRef(false);
  useEffect(() => {
    if (renderAfterAssistant.current && !busy) { renderAfterAssistant.current = false; save(); }
  });

  return (
    <Dialog open={open} onOpenChange={(value) => !busy && onOpenChange(value)}>
      <DialogContent className="bg-surface-950 border-surface-800 text-foreground sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="clip-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Scissors className="h-5 w-5 text-cyan-400" /> Edit clip
          </DialogTitle>
          <DialogDescription className="text-sm text-copy-500">
            Correct captions or change the style without uploading again. Saving updates only this clip; caption corrections keep the original audio and selected moment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-2 min-w-0">
          {open && <ClipEditAssistant clipId={clip.id} getState={editorState} onApply={applyAssistantChanges}
            onRender={() => { renderAfterAssistant.current = true; }} disabled={busy} />}
          <div className="min-w-0">
            <Label className="text-copy-300 mb-3 block">Content style</Label>
            <TemplatePicker templates={templates} value={templateId} onChange={changeTemplate} prefix="editor-style" disabled={busy} loading={templatesLoading} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-copy-300">Trim</Label>
              <span className="text-xs text-copy-500">{fmt(range[0])} → {fmt(range[1])} · {Math.round(range[1] - range[0])}s</span>
            </div>
            <Slider data-testid="editor-trim-slider" value={range} min={0} max={Math.max(duration, clip.end)}
              step={0.5} minStepsBetweenThumbs={6} onValueChange={setRange} disabled={busy} />
          </div>

          <div>
            <Label className="text-copy-300 mb-3 block">Aspect ratio</Label>
            <div className="grid grid-cols-3 gap-2" data-testid="editor-aspect-group">
              {[
                { id: "9:16", label: "9:16", hint: "Shorts / Reels", box: "w-3.5 h-6" },
                { id: "1:1", label: "1:1", hint: "Square feed", box: "w-5 h-5" },
                { id: "16:9", label: "16:9", hint: "Landscape", box: "w-6 h-3.5" },
              ].map((a) => (
                <button key={a.id} type="button" data-testid={`editor-aspect-${a.id}`}
                  onClick={() => setAspect(a.id)} disabled={busy}
                  className={`rounded-xl border p-3 flex flex-col items-center gap-2 transition-colors ${
                    aspect === a.id ? "border-cyan-500 bg-cyan-500/10" : "border-surface-800 bg-surface-900 hover:border-surface-700"
                  }`}>
                  <span className={`${a.box} rounded-sm border-2 ${aspect === a.id ? "border-cyan-400" : "border-surface-600"}`} />
                  <span className="text-sm font-semibold">{a.label}</span>
                  <span className="text-[10px] text-copy-500">{a.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-surface-800 bg-surface-900 p-3">
            <div><Label className="text-copy-300">Automatic speaker framing</Label><p className="text-xs text-copy-400 mt-1">Reframe each shot; keep uncertain pairs together. Turn off to preserve the full source.</p></div>
            <Switch data-testid="editor-framing-switch" checked={autoFraming} onCheckedChange={setAutoFraming} disabled={busy} className="data-[state=checked]:bg-cyan-500 shrink-0" />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-surface-800 bg-surface-900 p-3">
            <Label className="text-copy-300">Captions</Label>
            <Switch data-testid="editor-captions-switch" checked={captions} onCheckedChange={setCaptions} disabled={busy}
              className="data-[state=checked]:bg-cyan-500" />
          </div>

          {open && <CaptionWordEditor clipId={clip.id} range={range} edits={captionEdits} captionsEnabled={captions} disabled={busy}
            onChange={(edits) => { setCaptionEdits(edits); setCaptionDirty(true); }} />}

          <div className="flex items-center justify-between rounded-xl border border-surface-800 bg-surface-900 p-3">
            <div><Label className="text-copy-300">Opening headline</Label><p className="text-xs text-copy-400">Editorial title, separate from spoken captions</p></div>
            <Switch data-testid="editor-headline-switch" checked={headline} onCheckedChange={setHeadline} disabled={busy} className="data-[state=checked]:bg-cyan-500" />
          </div>
          {captions && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-copy-300">Caption position</Label>
                <Select value={position} onValueChange={setPosition} disabled={busy}>
                  <SelectTrigger data-testid="editor-position-select" className="bg-surface-900 border-surface-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface-900 border-surface-800 text-foreground">
                    <SelectItem value="top">Top</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="bottom">Bottom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {position !== "center" && (
                <div className="space-y-1.5">
                  <Label className="text-copy-300">{offset ? `Edge distance · ${offset}px` : "Auto safe area"}</Label>
                  <Slider data-testid="editor-offset-slider" value={[offset]} min={0} max={700} step={20} onValueChange={([v]) => setOffset(v)} disabled={busy} className="mt-3" />
                </div>
              )}
            </div>
          )}

          {busy && <p data-testid="editor-render-status" role="status" className="text-xs leading-relaxed text-cyan-300">Rendering captions and video, then saving your download. This can take a minute. Your previous version stays available until the new one is saved.</p>}
          <Button data-testid="editor-save-btn" onClick={save} disabled={busy}
            className="w-full rounded-full bg-cyan-500 text-black hover:bg-cyan-400 font-semibold h-11">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Re-rendering…</> : "Save & re-render"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
